import React, { useState, useEffect } from 'react';
import { CourtPOI, PickupGame, AthleteProfile } from '../types';
import { triggerHaptic } from '../utils/haptics';
import { 
  Zap, 
  MapPin, 
  Users, 
  CheckCircle2, 
  Navigation, 
  Sparkles, 
  Clock, 
  X, 
  Flame, 
  Trophy, 
  ArrowRight,
  ShieldCheck,
  Sun,
  Star,
  Activity
} from 'lucide-react';

interface QuickMatchModalProps {
  isOpen: boolean;
  onClose: () => void;
  courts: CourtPOI[];
  pickupGames: PickupGame[];
  user: AthleteProfile;
  onCheckInSuccess: (court: CourtPOI) => void;
  onNavigateToTab: (tab: any) => void;
}

const DEFAULT_FALLBACK_COURTS: CourtPOI[] = [
  {
    id: 'court_rucker_1',
    name: 'Rucker Park Main Court',
    address: '155th St & Frederick Douglass Blvd, New York, NY',
    sport: 'basketball',
    lat: 40.8296,
    lng: -73.9362,
    distanceKm: 0.8,
    rating: 4.9,
    activePlayersNow: 14,
    lighting: true,
    surfaceType: 'Pro Asphalt',
    hasUpcomingGame: true,
    geofenceRadiusMeters: 100,
    imageUrl: 'https://images.unsplash.com/photo-1546519638-68e109498ffc?auto=format&fit=crop&w=800&q=80',
    rimCondition: 'Pro Breakaway Glass',
    waterFountain: true,
    parkingAvailable: true,
  },
  {
    id: 'court_venice_2',
    name: 'Venice Beach Boardwalk Courts',
    address: '1800 Ocean Front Walk, Venice, CA',
    sport: 'basketball',
    lat: 33.9850,
    lng: -118.4695,
    distanceKm: 1.2,
    rating: 4.8,
    activePlayersNow: 18,
    lighting: true,
    surfaceType: 'Polyurethane',
    hasUpcomingGame: true,
    geofenceRadiusMeters: 120,
    imageUrl: 'https://images.unsplash.com/photo-1519766304817-4f37bda74a29?auto=format&fit=crop&w=800&q=80',
    rimCondition: 'Padded Steel',
    waterFountain: true,
  },
  {
    id: 'court_halsted_3',
    name: 'Lincoln Park Playground Complex',
    address: '2045 N Lincoln Park W, Chicago, IL',
    sport: 'soccer',
    lat: 41.9188,
    lng: -87.6358,
    distanceKm: 1.5,
    rating: 4.7,
    activePlayersNow: 10,
    lighting: true,
    surfaceType: 'Artificial Turf',
    hasUpcomingGame: true,
    geofenceRadiusMeters: 150,
    imageUrl: 'https://images.unsplash.com/photo-1574629810360-7efbbe195018?auto=format&fit=crop&w=800&q=80',
  }
];

export const QuickMatchModal: React.FC<QuickMatchModalProps> = ({
  isOpen,
  onClose,
  courts,
  pickupGames,
  user,
  onCheckInSuccess,
  onNavigateToTab,
}) => {
  const [nearestCourt, setNearestCourt] = useState<CourtPOI | null>(null);
  const [isLocating, setIsLocating] = useState<boolean>(true);
  const [hasCheckedIn, setHasCheckedIn] = useState<boolean>(false);
  const [userCoords, setUserCoords] = useState<{ lat: number; lng: number } | null>(null);

  useEffect(() => {
    if (!isOpen) return;

    setIsLocating(true);
    setHasCheckedIn(false);

    // Try to get browser geolocation or use user's saved location
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const lat = pos.coords.latitude;
          const lng = pos.coords.longitude;
          setUserCoords({ lat, lng });
          findAndSetNearestCourt(lat, lng);
        },
        (err) => {
          console.warn('Geolocation fallback:', err);
          // Fallback to user saved location or first active court
          const lat = user?.location?.lat || 40.7128;
          const lng = user?.location?.lng || -74.0060;
          findAndSetNearestCourt(lat, lng);
        },
        { timeout: 5000 }
      );
    } else {
      const lat = user?.location?.lat || 40.7128;
      const lng = user?.location?.lng || -74.0060;
      findAndSetNearestCourt(lat, lng);
    }
  }, [isOpen, courts, user]);

  const findAndSetNearestCourt = (lat: number, lng: number) => {
    const activeList = courts && courts.length > 0 ? courts : DEFAULT_FALLBACK_COURTS;

    // Calculate approximate distance
    const withDistances = activeList.map((c) => {
      const dLat = (c.lat - lat) * (Math.PI / 180);
      const dLng = (c.lng - lng) * (Math.PI / 180);
      const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(lat * (Math.PI / 180)) * Math.cos(c.lat * (Math.PI / 180)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
      const cCalc = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      const distanceMiles = Math.round((6371 * cCalc * 0.621371) * 10) / 10;
      return { ...c, computedMiles: distanceMiles };
    });

    // Sort by active players first (more active) then by distance
    withDistances.sort((a, b) => {
      if (b.activePlayersNow !== a.activePlayersNow) {
        return b.activePlayersNow - a.activePlayersNow;
      }
      return a.computedMiles - b.computedMiles;
    });

    setNearestCourt(withDistances[0] || DEFAULT_FALLBACK_COURTS[0]);
    setIsLocating(false);
  };

  const handleOneTapCheckIn = () => {
    if (!nearestCourt) return;
    triggerHaptic('success');
    setHasCheckedIn(true);

    // Save active check-in in local storage
    const checkInRecord = {
      courtId: nearestCourt.id,
      courtName: nearestCourt.name,
      timestamp: new Date().toISOString(),
      sport: nearestCourt.sport,
    };
    localStorage.setItem('playground_active_checkin', JSON.stringify(checkInRecord));

    onCheckInSuccess(nearestCourt);
  };

  if (!isOpen) return null;

  // Matching pickup game if any
  const matchedPickupGame = pickupGames?.find((g) => g.courtId === nearestCourt?.id || g.courtName?.includes(nearestCourt?.name || '') || g.address?.includes(nearestCourt?.name || ''));

  return (
    <div 
      className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center p-4 overflow-y-auto animate-fadeIn"
      onClick={() => {
        console.log('[QuickMatchModal] Outer backdrop clicked!');
        triggerHaptic('light');
        onClose();
      }}
    >
      <div 
        className="bg-gradient-to-br from-indigo-950 via-slate-900 to-indigo-950 rounded-[2.5rem] border-2 border-lime-400 p-6 md:p-8 max-w-lg w-full space-y-6 shadow-2xl relative overflow-hidden"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        {/* Background ambient glow */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-lime-400/10 rounded-full blur-3xl pointer-events-none" />

        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            console.log('[QuickMatchModal] Close X button clicked!');
            triggerHaptic('light');
            onClose();
          }}
          className="absolute top-5 right-5 p-2.5 bg-indigo-900 hover:bg-indigo-800 text-white hover:text-lime-400 rounded-full transition border border-white/20 z-30 cursor-pointer shadow-lg active:scale-95"
          aria-label="Close modal"
        >
          <X className="w-5 h-5 stroke-[2.5]" />
        </button>

        {/* Modal Title */}
        <div className="flex items-center space-x-3 relative z-10">
          <div className="p-3 bg-gradient-to-tr from-lime-400 to-emerald-400 text-black rounded-2xl shadow-lg shadow-lime-400/20">
            <Zap className="w-6 h-6 stroke-[2.5]" />
          </div>
          <div>
            <span className="px-2.5 py-0.5 bg-lime-400 text-black text-[10px] font-mono font-black rounded-full uppercase">
              Instant GPS Matchmaking ⚡
            </span>
            <h2 className="text-xl font-black italic uppercase text-white tracking-wide mt-0.5">
              Nearest Active Court Found!
            </h2>
          </div>
        </div>

        {isLocating ? (
          <div className="p-12 text-center space-y-3 bg-indigo-950/60 rounded-3xl border border-white/10">
            <Navigation className="w-8 h-8 text-lime-400 animate-spin mx-auto" />
            <p className="text-xs font-mono font-bold text-indigo-200">
              Scanning GPS coordinates for active runs...
            </p>
          </div>
        ) : nearestCourt ? (
          <div className="space-y-5 relative z-10">
            {/* Court Preview Banner Card */}
            <div className="relative rounded-3xl overflow-hidden border border-lime-400/40 shadow-xl bg-indigo-950">
              <img
                src={nearestCourt.imageUrl}
                alt={nearestCourt.name}
                className="w-full h-44 object-cover brightness-75"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-indigo-950 via-indigo-950/40 to-transparent p-5 flex flex-col justify-end">
                <div className="flex items-center justify-between">
                  <span className="px-2.5 py-1 bg-lime-400 text-black text-[10px] font-mono font-black rounded-lg uppercase shadow">
                    🔥 {nearestCourt.activePlayersNow} Active Players
                  </span>
                  <span className="px-2.5 py-1 bg-indigo-900/90 text-lime-300 border border-lime-400/30 text-[10px] font-mono font-bold rounded-lg backdrop-blur-md">
                    📍 {(nearestCourt as any).computedMiles ?? nearestCourt.distanceKm} Mi Away
                  </span>
                </div>

                <h3 className="text-lg font-black text-white italic mt-1 leading-tight">
                  {nearestCourt.name}
                </h3>
                <p className="text-xs text-indigo-200/90 font-medium flex items-center space-x-1">
                  <MapPin className="w-3.5 h-3.5 text-lime-400 shrink-0" />
                  <span className="truncate">{nearestCourt.address}</span>
                </p>
              </div>
            </div>

            {/* Court Badges Grid */}
            <div className="grid grid-cols-3 gap-2 text-center font-mono">
              <div className="p-2.5 bg-indigo-950/80 rounded-2xl border border-white/10 space-y-0.5">
                <span className="text-[9px] text-indigo-300 block uppercase font-bold">Surface</span>
                <span className="text-xs font-bold text-white truncate block">{nearestCourt.surfaceType}</span>
              </div>
              <div className="p-2.5 bg-indigo-950/80 rounded-2xl border border-white/10 space-y-0.5">
                <span className="text-[9px] text-indigo-300 block uppercase font-bold">Lighting</span>
                <span className="text-xs font-bold text-lime-400 block">{nearestCourt.lighting ? 'Night Lights On' : 'Daylight Only'}</span>
              </div>
              <div className="p-2.5 bg-indigo-950/80 rounded-2xl border border-white/10 space-y-0.5">
                <span className="text-[9px] text-indigo-300 block uppercase font-bold">Rating</span>
                <span className="text-xs font-bold text-amber-300 block">⭐ {nearestCourt.rating} / 5.0</span>
              </div>
            </div>

            {/* Active Run Banner */}
            {matchedPickupGame ? (
              <div className="p-3.5 bg-lime-400/10 border border-lime-400/30 rounded-2xl flex items-center space-x-3">
                <Trophy className="w-5 h-5 text-lime-400 shrink-0" />
                <div className="text-xs space-y-0.5">
                  <span className="font-extrabold text-lime-300 block">{matchedPickupGame.title}</span>
                  <span className="text-indigo-200 text-[11px]">
                    {matchedPickupGame.currentPlayers}/{matchedPickupGame.maxPlayers} Players • Organized run starting now!
                  </span>
                </div>
              </div>
            ) : (
              <div className="p-3 bg-indigo-900/60 border border-white/10 rounded-2xl flex items-center space-x-2 text-xs text-indigo-200 font-mono">
                <Activity className="w-4 h-4 text-lime-400 shrink-0" />
                <span>Open Pickup Court • Check-in to start or join a run immediately.</span>
              </div>
            )}

            {/* One-Tap Action Controls */}
            {!hasCheckedIn ? (
              <button
                type="button"
                onClick={handleOneTapCheckIn}
                className="w-full py-4 bg-gradient-to-r from-lime-400 via-emerald-400 to-lime-400 text-black font-black italic uppercase text-sm rounded-2xl shadow-xl shadow-lime-400/30 hover:scale-[1.02] active:scale-95 transition flex items-center justify-center space-x-2 cursor-pointer"
              >
                <Sparkles className="w-5 h-5 fill-black" />
                <span>One-Tap Check-In & Join Run (+100 XP)</span>
              </button>
            ) : (
              <div className="space-y-3 animate-fadeIn">
                <div className="p-4 bg-lime-400/20 text-lime-300 font-black italic text-xs rounded-2xl border border-lime-400/50 text-center space-y-1">
                  <div className="flex items-center justify-center space-x-1.5 text-sm">
                    <CheckCircle2 className="w-5 h-5 text-lime-400" />
                    <span>CHECKED IN AT {nearestCourt.name.toUpperCase()}!</span>
                  </div>
                  <p className="text-[11px] font-mono text-indigo-200">
                    +100 XP awarded to profile. You are now live on court radar!
                  </p>
                </div>

                <div className="flex items-center space-x-2">
                  <button
                    type="button"
                    onClick={() => {
                      onClose();
                      onNavigateToTab('map');
                    }}
                    className="flex-1 py-3 bg-indigo-900 hover:bg-indigo-800 text-white font-extrabold text-xs rounded-xl border border-white/15 transition flex items-center justify-center space-x-1.5"
                  >
                    <span>View Court Map</span>
                    <ArrowRight className="w-4 h-4" />
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      onClose();
                      onNavigateToTab('games');
                    }}
                    className="flex-1 py-3 bg-lime-400 text-black font-black italic text-xs rounded-xl shadow transition flex items-center justify-center space-x-1.5"
                  >
                    <span>Pickup Games</span>
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
};
